import React from 'react'
import { Config } from '../constants'
import { API } from '../http'
import { isUserLoggedIn, logUserOut } from '../contexts'

export type SessionStatus = 'Active' | 'ExpiringSoon' | 'Expired'

export interface SessionState {
	status: SessionStatus
	secondsRemaining: number
}

export function useSessionCheck(): [SessionState, () => void] {

	const [secondsRemaining, setSecondsRemaining] = React.useState(Number.MAX_SAFE_INTEGER)

	// external application can use this function to extend the session 
	const handleSessionRenewal = async () => {
		const extendOk = await API.extendSession()

		if (extendOk == 'ok') {

			const sessionCheck = await API.checkSession()

			if (sessionCheck.expiresIn) {
				setSecondsRemaining(sessionCheck.expiresIn)
			}
			else logUserOut()
		}
	}

	const doSessionCheck = async () => {
		const sessionCheck = await API.checkSession()

		if (!sessionCheck.expiresIn) {
			// session has expired, let's log the user out
			logUserOut()
		}
		else {
			setSecondsRemaining(sessionCheck.expiresIn)
		}
	}

	const doEffect = () => {
		// if user isn't logged in they can view the registration page indefinitely
		if (isUserLoggedIn()) {

			if (secondsRemaining == Number.MAX_SAFE_INTEGER) {
				// we're running our first session check
				doSessionCheck()
			} else {
				// we schedule our next one
				const timeoutSeconds = secondsRemaining <= Config.SessionWarningThreshold ?
					secondsRemaining + 1 :
					secondsRemaining - Config.SessionWarningThreshold + 1

				const timer = setTimeout(() => {
					doSessionCheck()
				}, timeoutSeconds * 1000)

				return () => clearTimeout(timer)
			}
		}
	}

	React.useEffect(doEffect, [secondsRemaining])

	const status: SessionStatus = 	secondsRemaining > Config.SessionWarningThreshold ? 'Active' : 
									secondsRemaining > 0 ? 'ExpiringSoon' : 
									'Expired'

	const sessionState: SessionState = { secondsRemaining, status }

	return [sessionState, handleSessionRenewal]
}